import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useTable, useGlobalFilter, useSortBy, usePagination } from 'react-table';
import 'bootstrap/dist/css/bootstrap.min.css';
import './DonationsList.css';
import { exportToExcel } from '../../../utils/excelExport';
import { faFileExcel, faCalendarDay, faCalendarAlt, faSync, faSearch, faChevronRight, faSave, faInfoCircle, faChartLine, faCheckCircle, faHourglassHalf, faMoneyBillWave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DonationService } from '../../../Services/Axios/Api';
import { toast } from 'react-toastify';
import { Modal, Button, Form, Card, InputGroup, Badge, OverlayTrigger, Tooltip, Tabs, Tab } from 'react-bootstrap';
import DonationAnalytics from './DonationAnalytics';

const formatDateTime = (value) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const pad = (n) => String(n).padStart(2, '0');
    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

const formatAmountWithCurrency = (amount, currency) => {
    const num = typeof amount === 'number' ? amount : Number(amount);
    const formattedAmount = Number.isFinite(num) ? num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : amount;
    return currency ? `${formattedAmount} ${currency}` : `${formattedAmount}`;
};

const buildDateBounds = ({ startDate, endDate }) => {
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    if (start && !Number.isNaN(start.getTime())) {
        start.setHours(0, 0, 0, 0);
    }
    if (end && !Number.isNaN(end.getTime())) {
        end.setHours(23, 59, 59, 999);
    }

    return { start: start && !Number.isNaN(start.getTime()) ? start : null, end: end && !Number.isNaN(end.getTime()) ? end : null };
};

const filterDonationsByDate = (donations, filters) => {
    const { start, end } = buildDateBounds(filters);
    if (!start && !end) return donations;

    return donations.filter(donation => {
        const donationDate = new Date(donation.donationDate);
        if (Number.isNaN(donationDate.getTime())) return false;
        if (start && donationDate < start) return false;
        if (end && donationDate > end) return false;
        return true;
    });
};

const PAYMENT_STATUSES = [
    'Completed', 'LinkGenerated', 'Pending', 'PaymentInitiated',
    'Processing', 'PaymentSuccessful', 'PaymentFailed', 'Cancelled',
    'Expired', 'Refunded', 'Error', 'InvalidPayment', 'GatewayError'
];

const PAYMENT_METHODS = ['PayPal', 'Fawry', 'AhlyMomken', 'Noon'];

// ===== Summary Statistics Bar =====
const SummaryBar = ({ summary, loading }) => {
    if (loading || !summary) {
        return (
            <div className="donations-summary-bar">
                {[1, 2, 3, 4].map(i => (
                    <div key={i} className="donations-stat-card">
                        <div className="placeholder-glow">
                            <span className="placeholder col-6"></span>
                            <span className="placeholder col-8 mt-2"></span>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="donations-summary-bar">
            <div className="donations-stat-card">
                <div className="stat-icon total"><FontAwesomeIcon icon={faChartLine} /></div>
                <div className="stat-value">{summary.totalCount}</div>
                <div className="stat-label">Total Donations</div>
                <div className="stat-sub">{summary.failedCount} failed</div>
            </div>
            <div className="donations-stat-card">
                <div className="stat-icon completed"><FontAwesomeIcon icon={faCheckCircle} /></div>
                <div className="stat-value">{formatAmountWithCurrency(summary.completedAmount, '')}</div>
                <div className="stat-label">Completed Amount</div>
                <div className="stat-sub">{summary.completedCount} transactions</div>
            </div>
            <div className="donations-stat-card">
                <div className="stat-icon pending"><FontAwesomeIcon icon={faHourglassHalf} /></div>
                <div className="stat-value">{summary.pendingCount}</div>
                <div className="stat-label">Pending</div>
                <div className="stat-sub">{formatAmountWithCurrency(summary.pendingAmount, '')} total</div>
            </div>
            <div className="donations-stat-card">
                <div className="stat-icon average"><FontAwesomeIcon icon={faMoneyBillWave} /></div>
                <div className="stat-value">{formatAmountWithCurrency(summary.averageAmount, '')}</div>
                <div className="stat-label">Average Donation</div>
                <div className="stat-sub">
                    {summary.byPaymentMethod?.length || 0} payment methods
                </div>
            </div>
        </div>
    );
};

// ===== Expandable Detail Panel =====
const DetailPanel = ({ donation }) => {
    const donor = donation.donorInfo;
    const isValid = (val) => val && val.toLowerCase() !== 'string';

    const extractGatewayDetails = () => {
        if (!donation.paymentDetailsJson) return null;
        try {
            const data = JSON.parse(donation.paymentDetailsJson);
            if (donation.paymentMethod === 'Fawry') {
               return {
                   'Fees': data.fawryFees ? `${data.fawryFees} EGP` : '—',
                   'Method Type': data.paymentMethod || '—',
                   '3DS ECI': data.threeDSInfo?.eci || '—',
                   '3DS Enrolled': data.threeDSInfo?.enrolled || '—',
                   'Gateway Status': data.orderStatus || '—'
               };
            } else if (donation.paymentMethod === 'Noon') {
                const order = data.result?.order || {};
                const tx = data.result?.transactions?.[0] || {};
                return {
                    'Brand': order.paymentData?.brand || order.paymentData?.paymentType || tx.paymentData?.paymentType || '—',
                    'Acquirer Bank': tx.authorization?.acquirer || '—',
                    'Provider Amount': tx.amount && `${tx.amount} ${tx.currency || ''}`,
                    'Network': tx.authorization?.network || '—'
                };
            } else if (donation.paymentMethod === 'PayPal') {
                const payer = data.payer || {};
                const purchase = data.purchase_units?.[0] || {};
                const capture = purchase.payments?.captures?.[0] || {};
                return {
                    'PayPal Payer': payer.email_address || '—',
                    'Seller Fee': capture.seller_receivable_breakdown?.paypal_fee?.value 
                                   ? `${capture.seller_receivable_breakdown.paypal_fee.value} ${capture.seller_receivable_breakdown.paypal_fee.currency_code}`
                                   : '—',
                    'Net Amount': capture.seller_receivable_breakdown?.net_amount?.value || '—'
                };
            }
            // Fallback for raw JSON dump
            return { 'Raw Extra Data': 'Available (Click to View)' };
        } catch (e) {
            return null;
        }
    };
    
    const gatewayDetails = extractGatewayDetails();

    return (
        <div className="donation-detail-panel">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0 text-muted">
                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                    Donation #{donation.donationId} — Full Details
                </h6>
            </div>

            <div className="donation-detail-grid">
                {/* Transaction Details */}
                <div className="detail-section">
                    <h6>Transaction Details</h6>
                    <div className="detail-field">
                        <span className="field-label">Donation ID</span>
                        <span className="field-value">#{donation.donationId}</span>
                    </div>
                    <div className="detail-field">
                        <span className="field-label">Transaction ID</span>
                        <span className="field-value">{donation.transactionId || <span className="empty">—</span>}</span>
                    </div>
                    <div className="detail-field">
                        <span className="field-label">Gateway Ref</span>
                        <span className={`field-value ${!donation.gatewayReference ? 'empty' : ''}`}>
                            {donation.gatewayReference || '—'}
                        </span>
                    </div>
                    <div className="detail-field">
                        <span className="field-label">Payment Method</span>
                        <span className="field-value">{donation.paymentMethod}</span>
                    </div>
                    <div className="detail-field">
                        <span className="field-label">Status</span>
                        <span className="field-value">
                            <span className={`donation-status-badge ${donation.paymentStatus?.toLowerCase()}`}>
                                {donation.paymentStatus}
                            </span>
                        </span>
                    </div>
                    <div className="detail-field">
                        <span className="field-label">Donation Date</span>
                        <span className="field-value">{formatDateTime(donation.donationDate)}</span>
                    </div>
                    <div className="detail-field">
                        <span className="field-label">Completed Date</span>
                        <span className={`field-value ${!donation.paymentCompletedDate ? 'empty' : ''}`}>
                            {donation.paymentCompletedDate ? formatDateTime(donation.paymentCompletedDate) : '—'}
                        </span>
                    </div>
                    <div className="detail-field">
                        <span className="field-label">Amount</span>
                        <span className="field-value fw-bold">{formatAmountWithCurrency(donation.amount, donation.currency)}</span>
                    </div>
                    <div className="detail-field">
                        <span className="field-label">Campaign</span>
                        <span className="field-value">{donation.campaignName}</span>
                    </div>
                    <div className="detail-field">
                        <span className="field-label">Flags</span>
                        <span className="field-value">
                            {donation.isAnonymous && <span className="donation-badge anonymous">🔒 Anonymous</span>}
                            {' '}
                            {donation.isRecurring && <span className="donation-badge recurring">🔄 Recurring</span>}
                            {!donation.isAnonymous && !donation.isRecurring && <span className="empty">—</span>}
                        </span>
                    </div>
                </div>

                {/* Gateway Specific Insights */}
               

                {/* Donor Information */}
                <div className="detail-section">
                    <h6>Donor Information</h6>
                    {donor ? (
                        <>
                            <div className="detail-field">
                                <span className="field-label">Name</span>
                                <span className="field-value">{donation.isAnonymous ? 'Anonymous' : donor.name}</span>
                            </div>
                            <div className="detail-field">
                                <span className="field-label">Email</span>
                                <span className="field-value">{donor.email}</span>
                            </div>
                            <div className="detail-field">
                                <span className="field-label">Phone</span>
                                <span className={`field-value ${!isValid(donor.phoneNumber) ? 'empty' : ''}`}>
                                    {isValid(donor.phoneNumber) ? donor.phoneNumber : '—'}
                                </span>
                            </div>
                            <div className="detail-field">
                                <span className="field-label">Address</span>
                                <span className={`field-value ${!isValid(donor.address) ? 'empty' : ''}`}>
                                    {isValid(donor.address) ? donor.address : '—'}
                                </span>
                            </div>
                            <div className="detail-field">
                                <span className="field-label">City</span>
                                <span className={`field-value ${!isValid(donor.city) ? 'empty' : ''}`}>
                                    {isValid(donor.city) ? donor.city : '—'}
                                </span>
                            </div>
                            <div className="detail-field">
                                <span className="field-label">Country</span>
                                <span className={`field-value ${!isValid(donor.country) ? 'empty' : ''}`}>
                                    {isValid(donor.country) ? donor.country : '—'}
                                </span>
                            </div>
                            <div className="detail-field">
                                <span className="field-label">Postal Code</span>
                                <span className={`field-value ${!isValid(donor.postalCode) ? 'empty' : ''}`}>
                                    {isValid(donor.postalCode) ? donor.postalCode : '—'}
                                </span>
                            </div>
                        </>
                    ) : (
                        <div className="text-muted text-center py-3">No donor information</div>
                    )}

                    {/* Donor Message */}
                    {donation.message && (
                        <div className="mt-3 pt-3 border-top">
                            <div className="field-label mb-1">Donor Message</div>
                            <div className="p-2 bg-light rounded" style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
                                "{donation.message}"
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ===== Main Component =====
const DonationsList = () => {
    const [donationData, setDonationData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showExportModal, setShowExportModal] = useState(false);
    const [expandedRows, setExpandedRows] = useState({});
    const [summaryData, setSummaryData] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [methodFilter, setMethodFilter] = useState('');
    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
    const [filters, setFilters] = useState({ startDate: '', endDate: '' });

    useEffect(() => {
        loadDonations();
        loadSummary();
        loadAnalytics();
    }, []);

    const loadDonations = async () => {
        try {
            setLoading(true);
            const response = await DonationService.getDonations();
            if (response.isSuccess) {
                setDonationData(response.result);
            } else {
                toast.error('Failed to load donations');
            }
        } catch (error) {
            toast.error(error.message || 'Failed to load donations');
        } finally {
            setLoading(false);
        }
    };

    const loadSummary = async () => {
        try {
            setSummaryLoading(true);
            const response = await DonationService.getDonationSummary();
            if (response.isSuccess) {
                setSummaryData(response.result);
            }
        } catch (error) {
            console.error('Failed to load summary:', error);
        } finally {
            setSummaryLoading(false);
        }
    };

    const loadAnalytics = async () => {
        try {
            const response = await DonationService.getDonationAnalytics(30);
            if (response.isSuccess) {
                setAnalyticsData(response.result);
            }
        } catch (error) {
            console.error('Failed to load analytics:', error);
        }
    };

    const handleNotesUpdate = useCallback((donationId, newNotes) => {
        setDonationData(prev =>
            prev.map(d => d.donationId === donationId ? { ...d, notes: newNotes } : d)
        );
    }, []);

    const toggleRowExpand = (rowId) => {
        setExpandedRows(prev => ({ ...prev, [rowId]: !prev[rowId] }));
    };

    const filteredDonations = useMemo(() => {
        let data = filterDonationsByDate(donationData, filters);
        if (statusFilter) {
            data = data.filter(d => d.paymentStatus === statusFilter);
        }
        if (methodFilter) {
            data = data.filter(d => d.paymentMethod === methodFilter);
        }
        return data;
    }, [donationData, filters, statusFilter, methodFilter]);

    const columns = useMemo(
        () => [
            {
                Header: '',
                id: 'expander',
                Cell: ({ row }) => (
                    <button
                        className={`row-expand-btn ${expandedRows[row.original.donationId] ? 'expanded' : ''}`}
                        onClick={(e) => { e.stopPropagation(); toggleRowExpand(row.original.donationId); }}
                        title="View details"
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                ),
                disableSortBy: true,
            },
            {
                Header: 'ID',
                accessor: 'donationId',
                Cell: ({ value }) => <span className="text-muted">#{value}</span>,
            },
            {
                Header: 'Donor',
                accessor: row => row.donorInfo?.isAnonymous || row.isAnonymous ? 'Anonymous' : row.donorInfo?.name,
                Cell: ({ row }) => {
                    const d = row.original;
                    const name = d.donorInfo?.isAnonymous || d.isAnonymous ? 'Anonymous' : d.donorInfo?.name;
                    return (
                        <div>
                            <div className="fw-medium">{name}</div>
                            <div className="text-muted" style={{ fontSize: '0.78rem' }}>{d.donorInfo?.email}</div>
                        </div>
                    );
                },
            },
            {
                Header: 'Campaign',
                accessor: 'campaignName',
                Cell: ({ row }) => (
                    <div>
                        <div>{row.original.campaignName}</div>
                        {row.original.isRecurring && (
                            <span className="donation-badge recurring">🔄 Recurring</span>
                        )}
                    </div>
                ),
            },
            {
                Header: 'Amount',
                accessor: 'amount',
                Cell: ({ row }) => {
                    const { amount, currency } = row.original;
                    return <span className="fw-bold">{formatAmountWithCurrency(amount, currency)}</span>;
                },
            },
            {
                Header: 'Method',
                accessor: 'paymentMethod',
            },
            {
                Header: 'Date',
                accessor: 'donationDate',
                Cell: ({ value }) => formatDateTime(value),
            },
            {
                Header: 'Status',
                accessor: 'paymentStatus',
                Cell: ({ value }) => (
                    <span className={`donation-status-badge ${value?.toLowerCase()}`}>
                        {value}
                    </span>
                ),
            },

        ],
        [expandedRows]
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        state,
        setGlobalFilter,
        pageOptions,
        canPreviousPage,
        canNextPage,
        nextPage,
        previousPage,
        gotoPage,
        setPageSize,
    } = useTable(
        {
            columns,
            data: filteredDonations,
            initialState: {
                sortBy: [{ id: 'donationDate', desc: true }],
                pageSize: 10,
            }
        },
        useGlobalFilter,
        useSortBy,
        usePagination
    );

    const { globalFilter, pageIndex, pageSize } = state;

    const handleExportClick = () => {
        setShowExportModal(true);
    };

    const handleDateFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        gotoPage(0);
    };

    const clearAllFilters = () => {
        setFilters({ startDate: '', endDate: '' });
        setStatusFilter('');
        setMethodFilter('');
        setGlobalFilter('');
        gotoPage(0);
    };

    const handleExport = () => {
        const filteredData = filterDonationsByDate(donationData, {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        });

        if (!filteredData || filteredData.length === 0) {
            toast.error('No donations found for the selected date range');
            return;
        }

        try {
            const exportData = filteredData.map(donation => {
                const date = new Date(donation.donationDate);
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                const formattedDate = `${day}/${month}/${year}`;
                const completedDate = donation.paymentCompletedDate
                    ? formatDateTime(donation.paymentCompletedDate)
                    : '';

                return {
                    'Donation ID': donation.donationId,
                    'Donor Name': donation.isAnonymous ? 'Anonymous' : donation.donorInfo?.name,
                    'Email': donation.donorInfo?.email,
                    'Phone': donation.donorInfo?.phoneNumber,
                    'Address': donation.donorInfo?.address || '',
                    'City': donation.donorInfo?.city || '',
                    'Country': donation.donorInfo?.country || '',
                    'Postal Code': donation.donorInfo?.postalCode || '',
                    'Campaign Name': donation.campaignName,
                    'Campaign ID': donation.campaignId || '',
                    'Amount': `${donation.amount} ${donation.currency}`,
                    'Payment Method': donation.paymentMethod,
                    'Date': formattedDate,
                    'Payment Completed': completedDate,
                    'Status': donation.paymentStatus,
                    'Transaction ID': donation.transactionId,
                    'Gateway Reference': donation.gatewayReference || '',
                    'Message': donation.message || '',
                    'Anonymous': donation.isAnonymous ? 'Yes' : 'No',
                    'Recurring': donation.isRecurring ? 'Yes' : 'No',
                    'Admin Notes': donation.notes || ''
                };
            });

            exportToExcel(exportData, 'donations.xlsx');
            setShowExportModal(false);
            setDateRange({ startDate: '', endDate: '' });
            toast.success('Donations exported successfully');
        } catch (error) {
            toast.error('Failed to export donations');
            console.error('Export error:', error);
        }
    };

    if (loading) {
        return (
            <div className="container mt-5 text-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="container mt-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className='m-2'>Donations</h2>
                <div className='m-2'>
                    <button
                        className="btn btn-success me-2"
                        onClick={handleExportClick}
                    >
                        <FontAwesomeIcon icon={faFileExcel} className="me-2" />
                        Export to Excel
                    </button>
                </div>
            </div>

            <Tabs defaultActiveKey="data" id="donations-tabs" className="mb-4">
                <Tab eventKey="data" title={<span><FontAwesomeIcon icon={faSearch} className="me-2" />Donations Data</span>} className="pt-3">
                    {/* Summary Statistics Bar */}
                    <SummaryBar summary={summaryData} loading={summaryLoading} />

            {/* Filters Bar */}
            <Card className="shadow-sm border-0 bg-light mb-4">
                <Card.Body className="p-3">
                    <div className="d-flex flex-column flex-lg-row gap-3">
                        <div className="flex-grow-1">
                            <Form.Label className="mb-1 small text-muted">Search</Form.Label>
                            <InputGroup size="sm" className="bg-white rounded-2 shadow-sm">
                                <InputGroup.Text className="bg-white border-0 text-primary">
                                    <FontAwesomeIcon icon={faSearch} />
                                </InputGroup.Text>
                                <Form.Control
                                    type="search"
                                    placeholder="Search donations..."
                                    value={globalFilter || ''}
                                    onChange={e => setGlobalFilter(e.target.value)}
                                    className="border-0"
                                />
                            </InputGroup>
                        </div>
                        <div>
                            <Form.Label className="mb-1 small text-muted">Status</Form.Label>
                            <Form.Select
                                size="sm"
                                className="filter-dropdown shadow-sm"
                                value={statusFilter}
                                onChange={e => { setStatusFilter(e.target.value); gotoPage(0); }}
                            >
                                <option value="">All Statuses</option>
                                {PAYMENT_STATUSES.map(s => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </Form.Select>
                        </div>
                        <div>
                            <Form.Label className="mb-1 small text-muted">Method</Form.Label>
                            <Form.Select
                                size="sm"
                                className="filter-dropdown shadow-sm"
                                value={methodFilter}
                                onChange={e => { setMethodFilter(e.target.value); gotoPage(0); }}
                            >
                                <option value="">All Methods</option>
                                {PAYMENT_METHODS.map(m => (
                                    <option key={m} value={m}>{m}</option>
                                ))}
                            </Form.Select>
                        </div>
                        <div className="flex-grow-1">
                            <Form.Label className="mb-1 small text-muted">From</Form.Label>
                            <InputGroup size="sm" className="bg-white rounded-2 shadow-sm">
                                <InputGroup.Text className="bg-white border-0 text-primary">
                                    <FontAwesomeIcon icon={faCalendarAlt} />
                                </InputGroup.Text>
                                <Form.Control
                                    type="date"
                                    value={filters.startDate}
                                    onChange={(e) => handleDateFilterChange('startDate', e.target.value)}
                                    placeholder="From"
                                    className="border-0"
                                    
                                />
                            </InputGroup>
                        </div>
                        <div className="flex-grow-1">
                            <Form.Label className="mb-1 small text-muted">To</Form.Label>
                            <InputGroup size="sm" className="bg-white rounded-2 shadow-sm">
                                <InputGroup.Text className="bg-white border-0 text-primary">
                                    <FontAwesomeIcon icon={faCalendarDay} />
                                </InputGroup.Text>
                                <Form.Control
                                    type="date"
                                    value={filters.endDate}
                                    onChange={(e) => handleDateFilterChange('endDate', e.target.value)}
                                    placeholder="To"
                                    className="border-0"
                                />
                            </InputGroup>
                        </div>
                        <div className="d-flex align-items-end">
                            <div className="d-flex align-items-center gap-2">
                                <Button variant="outline-secondary" size="sm" onClick={clearAllFilters}>
                                    <FontAwesomeIcon icon={faSync} className="me-1" />
                                    Clear
                                </Button>
                            </div>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            {/* Results count */}
            <div className="mb-2 text-muted small">
                Showing {filteredDonations.length} of {donationData.length} donations
                {(statusFilter || methodFilter) && (
                    <span>
                        {' '}— filtered by
                        {statusFilter && <Badge bg="info" className="ms-1">{statusFilter}</Badge>}
                        {methodFilter && <Badge bg="secondary" className="ms-1">{methodFilter}</Badge>}
                    </span>
                )}
            </div>

            {/* Table */}
            <div className="donations-table-container">
                <table className="table table-hover mb-0" {...getTableProps()}>
                    <thead className="donations-table-header">
                        {headerGroups.map(headerGroup => {
                            const { key, ...restHeaderGroupProps } = headerGroup.getHeaderGroupProps();
                            return (
                                <tr key={key} {...restHeaderGroupProps}>
                                    {headerGroup.headers.map(column => {
                                        const { key: columnKey, ...restHeaderProps } = column.getHeaderProps(column.getSortByToggleProps());
                                        return (
                                            <th
                                                key={columnKey}
                                                className="donations-table-th"
                                                {...restHeaderProps}
                                            >
                                                {column.render('Header')}
                                                <span className="donations-sort-indicator">
                                                    {column.isSorted
                                                        ? column.isSortedDesc
                                                            ? ' 🔽'
                                                            : ' 🔼'
                                                        : ''}
                                                </span>
                                            </th>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {page.map(row => {
                            prepareRow(row);
                            const { key, ...restRowProps } = row.getRowProps();
                            const isExpanded = expandedRows[row.original.donationId];
                            return (
                                <React.Fragment key={key}>
                                    <tr
                                        {...restRowProps}
                                        onClick={() => toggleRowExpand(row.original.donationId)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {row.cells.map(cell => {
                                            const { key: cellKey, ...restCellProps } = cell.getCellProps();
                                            return (
                                                <td key={cellKey} {...restCellProps}>
                                                    {cell.render('Cell')}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {isExpanded && (
                                        <tr className="donation-detail-row">
                                            <td colSpan={columns.length}>
                                                <DetailPanel
                                                    donation={row.original}
                                                    onNotesUpdate={handleNotesUpdate}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="d-flex flex-column flex-md-row align-items-center justify-content-between mt-3 gap-2">
                <div className="d-flex align-items-center gap-2">
                    <span>Rows per page:</span>
                    <Form.Select
                        size="sm"
                        style={{ width: '90px' }}
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                    >
                        {[10, 20, 50, 100].map(size => (
                            <option key={size} value={size}>{size}</option>
                        ))}
                    </Form.Select>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <Button variant="outline-primary" size="sm" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                        {'<<'}
                    </Button>
                    <Button variant="outline-primary" size="sm" onClick={() => previousPage()} disabled={!canPreviousPage}>
                        {'<'}
                    </Button>
                    <span className="mx-2">
                        Page <strong>{pageIndex + 1}</strong> of <strong>{pageOptions.length}</strong>
                    </span>
                    <Form.Control
                        size="sm"
                        type="number"
                        min={1}
                        max={pageOptions.length}
                        value={pageIndex + 1}
                        onChange={(e) => {
                            const page = e.target.value ? Number(e.target.value) - 1 : 0;
                            gotoPage(page);
                        }}
                        style={{ width: '80px' }}
                    />
                    <Button variant="outline-primary" size="sm" onClick={() => nextPage()} disabled={!canNextPage}>
                        {'>'}
                    </Button>
                    <Button variant="outline-primary" size="sm" onClick={() => gotoPage(pageOptions.length - 1)} disabled={!canNextPage}>
                        {'>>'}
                    </Button>
                </div>
            </div>
            </Tab>

            <Tab eventKey="analytics" title={<span><FontAwesomeIcon icon={faChartLine} className="me-2" />Analytics</span>} className="pt-3">
                <DonationAnalytics analyticsData={analyticsData} />
            </Tab>
            </Tabs>

            {/* Export Modal */}
            <Modal show={showExportModal} onHide={() => setShowExportModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Export Donations</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted small mb-3">
                        Export includes all details: donor info, transaction IDs, gateway references, messages, admin notes, and more.
                    </p>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Start Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={dateRange.startDate}
                                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>End Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={dateRange.endDate}
                                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowExportModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="success" onClick={handleExport}>
                        Export
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default DonationsList;
